import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type AdminOrgBilling } from '@/api/adminClient';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { LoadError } from '@/components/ui/load-state';
import { OrgUsagePanel } from '@/components/usage/OrgUsagePanel';
import { MONEY_SECTIONS, type MoneyPanel, type MoneySection, orgMoneyPath, parseMoneyPanel } from '@/lib/orgNav';
import { keepAnchorInView } from '@/lib/scroll';
import { CreditHistoryPanel, GrantCreditPanel } from '@/pages/org/OrgCreditPanels';
import { useOrgScope } from '@/pages/org/OrgLayout';
import { OrgMoneyCards } from '@/pages/org/OrgMoneyCards';
import { MONEY_SECTION_TITLES, OrgMoneySection } from '@/pages/org/OrgMoneySection';
import { InvoicesPanel, PlanPanel, PromoPanel } from '@/pages/org/OrgPlanPanels';
import { useOrgMoney } from '@/pages/org/useOrgMoney';

/** The old Usage, Billing and Credits paths land on their part of Money. */
export function OrgMoneyRedirect({ section }: { section: MoneySection }) {
  const { orgId = '' } = useParams();
  return <Navigate to={orgMoneyPath(orgId, section)} replace />;
}

export function OrgMoneyPage() {
  const { orgId, reloadOrg } = useOrgScope();
  const { hash, search } = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const { billing, setBilling, lastGrant, loading, loadError, reload } = useOrgMoney(orgId);
  const [portalBusy, setPortalBusy] = useState(false);
  const panel = parseMoneyPanel(new URLSearchParams(search).get('panel'));

  useEffect(() => {
    const id = hash.slice(1);
    if (!id || !contentRef.current) return undefined;
    return keepAnchorInView(id, contentRef.current);
  }, [hash]);

  const setPanel = useCallback(
    (next?: MoneyPanel) => {
      const params = new URLSearchParams(search);
      if (next) params.set('panel', next);
      else params.delete('panel');
      const query = params.toString();
      navigate({ search: query ? `?${query}` : '', hash }, { replace: true });
    },
    [navigate, search, hash],
  );
  const closePanel = useCallback(() => setPanel(undefined), [setPanel]);
  const closeAndReload = useCallback(() => {
    closePanel();
    reload();
  }, [closePanel, reload]);
  const onBillingChange = useCallback(
    (next: AdminOrgBilling) => {
      setBilling(next);
      void reloadOrg().catch(() => undefined);
    },
    [setBilling, reloadOrg],
  );

  const openPortal = () => {
    setPortalBusy(true);
    adminClient
      .createPortal(orgId)
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch((err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to open the customer portal'))
      .finally(() => setPortalBusy(false));
  };

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Money"
        description="What this organization spends in Studio, the plan it pays for, and the credit it has left."
      />
      <nav aria-label="Money sections" className="-mt-2 mb-8 flex flex-wrap gap-2">
        {MONEY_SECTIONS.map((section) => (
          <Link
            key={section}
            to={orgMoneyPath(orgId, section)}
            className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {MONEY_SECTION_TITLES[section]}
          </Link>
        ))}
      </nav>
      <div ref={contentRef} className="flex flex-col gap-12">
        <OrgMoneySection
          id="spend"
          description="AI building is the cost of the models that write and change apps. Integrations are the services those apps call."
        >
          <OrgUsagePanel orgId={orgId} />
        </OrgMoneySection>
        <OrgMoneySection id="plan" description="The plan this organization pays for, the credit it has left, any discount, and its invoices.">
          {loading && !billing ? (
            <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading plan and billing
            </div>
          ) : !billing && loadError ? (
            <LoadError message={loadError} onRetry={reload} className="rounded-xl border border-border bg-card" />
          ) : (
            <OrgMoneyCards
              billing={billing}
              lastGrant={lastGrant}
              portalBusy={portalBusy}
              onOpenPanel={setPanel}
              onOpenPortal={openPortal}
            />
          )}
        </OrgMoneySection>
      </div>

      {billing && panel === 'plan' ? (
        <PlanPanel orgId={orgId} billing={billing} onBillingChange={onBillingChange} onClose={closePanel} />
      ) : null}
      {billing && panel === 'invoices' ? <InvoicesPanel billing={billing} onClose={closePanel} /> : null}
      {panel === 'promo' ? <PromoPanel orgId={orgId} onClose={closePanel} onApplied={closeAndReload} /> : null}
      {panel === 'grant-credit' ? <GrantCreditPanel orgId={orgId} onClose={closePanel} onGranted={closeAndReload} /> : null}
      {billing && panel === 'history' ? <CreditHistoryPanel orgId={orgId} billing={billing} onClose={closePanel} /> : null}
    </div>
  );
}
